import React, {useMemo} from 'react';
import classNames from 'classnames';
import {observer} from 'mobx-react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import {actions, categories, pushEvent} from '../../helpers/analyticsEvents';
import HeaderLogo from '../../public/images/newAmplifyLogoFullColor.svg';
import Plus from '../../components/icons/Plus';
import {usePartnerBrandings} from '../../components/CoBrandedLogo/usePartnerBrandings';
import styles from './Header.module.scss';
import MailTo from '../../public/images/mailTo.svg';
import PhoneTo from '../../public/images/phoneTo.svg';
import {useStore} from '../../components/StoreProvider';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CookieOutlinedIcon from '@mui/icons-material/CookieOutlined';
import {openCookiePreferences, TERMLY_PREFERENCES_CLASS} from '../../helpers/consent';

interface HeaderProps {
    scrollDirection: {isDown: boolean; isUp: boolean; y: number; headerHeight: number};
    className?: string;
    isHideAmplifyLogo?: boolean;
}

export default observer(
    React.forwardRef(function Header(
        {className, scrollDirection, isHideAmplifyLogo = false}: HeaderProps,
        ref: React.RefObject<HTMLDivElement>,
    ) {
        const store = useStore();
        const {logStore} = store;

        const {helpEmail, fartherHelpPhone, landing, displayedPhone} = store.constInfo;
        const params = new URLSearchParams(typeof window !== 'undefined' && window.location.search);
        const utmSource = params.get('utm_source')?.toLowerCase();
        const phoneNumber = utmSource === 'farther' ? fartherHelpPhone : displayedPhone;
        const {logoMap, strapiKeys} = usePartnerBrandings();
        const partnerLogoSrc = utmSource && strapiKeys.has(utmSource) ? logoMap[utmSource] : null;
        const [firstName] = store.userData.getField('firstName');
        const shouldHideHeader = useMemo(() => {
            if (window.scrollY > 0 && window.scrollY <= scrollDirection?.headerHeight) {
                return true;
            }
            if (Math.round(scrollDirection?.y) === 0) {
                return false;
            }
            return scrollDirection?.isDown;
        }, [scrollDirection]);

        const logClickAction = (action: string) => {
            logStore.logAction({
                action: action,
                data: {},
            });
        };

        const handleLogoClick = (e) => {
            e.preventDefault();
            pushEvent(categories.any, actions.land);
            logClickAction(actions.logoClicked);
            document.location.href = landing;
        };

        return (
            <div
                ref={ref}
                className={classNames(styles.header, className, {
                    [styles.hide]: shouldHideHeader,
                    [styles.show]: !shouldHideHeader,
                    [styles.withoutLogo]: isHideAmplifyLogo,
                })}
            >
                <Box
                    sx={{
                        display: {xs: isHideAmplifyLogo ? 'none' : 'initial', md: 'initial'},
                        ...(partnerLogoSrc ? {flex: {xs: 1, md: 'none'}, minWidth: 0, mr: {xs: '16px', md: 0}} : {}),
                    }}
                >
                    <Stack direction="row" alignItems="center" sx={{gap: {xs: '8px', md: '20px'}}}>
                        <Box
                            sx={{
                                ...(partnerLogoSrc
                                    ? {flex: {xs: 1, md: 'none'}, minWidth: 0, maxWidth: {md: 160}}
                                    : {}),
                                '& svg': {width: '100%', height: 'auto'},
                            }}
                        >
                            <a
                                aria-label="Amplify Home Page"
                                href={landing}
                                onClick={handleLogoClick}
                                style={{display: 'flex'}}
                            >
                                <HeaderLogo />
                            </a>
                        </Box>
                        {partnerLogoSrc && (
                            <>
                                <Box sx={{alignSelf: 'stretch', display: 'flex', alignItems: 'center', flexShrink: 0}}>
                                    <Plus sx={{width: {xs: 12, md: 17}, height: {xs: 12, md: 17}}} />
                                </Box>
                                <Box
                                    component="img"
                                    src={partnerLogoSrc}
                                    alt={utmSource}
                                    sx={{
                                        flex: {xs: 1, md: 'none'},
                                        minWidth: 0,
                                        maxWidth: {md: 128},
                                        objectFit: 'contain',
                                    }}
                                />
                            </>
                        )}
                    </Stack>
                </Box>
                <Stack direction="row" alignItems="center" gap={{xs: 3, sm: 6}} sx={{justifySelf: 'flex-end'}}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        component="a"
                        href={`mailto:${helpEmail}`}
                        gap={1}
                        onClick={() => logClickAction(actions.emailIconClicked)}
                    >
                        <MailTo />
                        <Box className={styles.phoneNumber} sx={{display: {xs: 'none', sm: 'block'}}}>
                            {helpEmail}
                        </Box>
                    </Stack>
                    <Stack
                        direction="row"
                        alignItems="center"
                        component="a"
                        href={`tel:${phoneNumber.href}`}
                        gap={1}
                        onClick={() => logClickAction(actions.phoneIconClicked)}
                    >
                        <PhoneTo />
                        <Box className={styles.phoneNumber} sx={{display: {xs: 'none', sm: 'block'}}}>
                            {phoneNumber.displayValue}
                        </Box>
                    </Stack>
                    <Stack
                        direction="row"
                        alignItems="center"
                        component="button"
                        type="button"
                        className={TERMLY_PREFERENCES_CLASS}
                        onClick={openCookiePreferences}
                        aria-label="Cookie preferences"
                        title="Cookie preferences"
                        sx={{
                            background: 'none',
                            border: 0,
                            padding: 0,
                            font: 'inherit',
                            color: 'inherit',
                            cursor: 'pointer',
                        }}
                    >
                        {/* Icon only — the accessible name comes from aria-label/title. */}
                        <CookieOutlinedIcon fontSize="large" />
                    </Stack>
                    {firstName && (
                        <Stack direction="row" alignItems="center" gap={1}>
                            <AccountCircleIcon fontSize="large" />
                            <Box className={styles.phoneNumber} sx={{display: {xs: 'none', sm: 'block'}}}>
                                {firstName}
                            </Box>
                        </Stack>
                    )}
                </Stack>
            </div>
        );
    }),
);
